import React, { useState, useEffect } from 'react'
import { Modal, Form, Input, Checkbox } from 'semantic-ui-react'
import { AppPrimaryButton, AppButton } from 'elements'
import { FlexRowBetween, TitleText, LoaderBar } from 'components'
import { X, Trash } from 'phosphor-react'
import { CustomerService } from 'utility/services/customer'
import { AuthService } from 'utility/services/auth'
import { useNotification } from 'hooks/toast'
import styled from 'styled-components'

const EmailListContainer = styled.div`
    max-height: 300px;
    overflow-y: auto;
    margin-top: 16px;
    padding: 8px;
    border: 1px solid #e8e9e9;
    border-radius: 4px;
`

const EmailItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px;
    margin-bottom: 8px;
    background: #f8f9fa;
    border-radius: 4px;
    
    &:last-child {
        margin-bottom: 0;
    }
`

const EmailText = styled.span`
    flex: 1;
    margin-right: 8px;
`

const DeleteIcon = styled(Trash)`
    cursor: pointer;
    color: #dc3545;
    &:hover {
        color: #c82333;
    }
`

export const CustomerNotificationModal = ({ open, onClose, customer }) => {
    const [emailInput, setEmailInput] = useState('')
    const [emailList, setEmailList] = useState([])
    const [autoEmail, setAutoEmail] = useState(false)
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [groupId, setGroupId] = useState(null)
    const { successToast, errorToast } = useNotification()

    const user = AuthService.getUser()
    const companyId = user?.CompanyId || user?.Companies?.[0]?.CompanyId
    const systemUserId = user?.unique_id || user?.SystemUserId || 0

    useEffect(() => {
        if (open && customer?.unique_id) {
            fetchNotificationData()
        } else if (open) {
            // Reset for new customer
            setEmailList([])
            setAutoEmail(false)
            setEmailInput('')
            setGroupId(null)
        }
    }, [open, customer])

    const fetchNotificationData = async () => {
        setLoading(true)
        try {
            const result = await CustomerService.getNotificationByCustomerId(customer.unique_id)
            // Handle response structure: { Success: true, Data: [{ ... }] } or direct object
            let notificationData = null
            if (result) {
                if (result.Data && Array.isArray(result.Data) && result.Data.length > 0) {
                    // Response wrapped in Data array
                    const [firstItem] = result.Data
                    notificationData = firstItem
                } else if (result.AutoEmail !== undefined || result.OtherEmails !== undefined) {
                    // Direct object response
                    notificationData = result
                }
            }

            if (notificationData) {
                setAutoEmail(notificationData.AutoEmail || false)
                setGroupId(notificationData.GroupId || null)
                // Parse OtherEmails string (semicolon-separated) into array
                const emails = notificationData.OtherEmails
                    ? notificationData.OtherEmails.split(';').map(e => e.trim()).filter(e => e)
                    : []
                setEmailList(emails)
            } else {
                setAutoEmail(false)
                setEmailList([])
                setGroupId(null)
            }
        } catch (error) {
            console.error('Error fetching notification data:', error)
            setAutoEmail(false)
            setEmailList([])
        } finally {
            setLoading(false)
        }
    }

    const handleAddEmail = () => {
        const email = emailInput.trim()
        if (!email) {
            errorToast('Please enter an email address')
            return
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(email)) {
            errorToast('Please enter a valid email address')
            return
        }

        if (emailList.includes(email)) {
            errorToast('This email is already in the list')
            return
        }

        setEmailList([...emailList, email])
        setEmailInput('')
    }

    const handleDeleteEmail = (emailToDelete) => {
        setEmailList(emailList.filter(email => email !== emailToDelete))
    }

    const handleDeleteNotification = async () => {
        if (!groupId) {
            errorToast('No notification group found to delete')
            return
        }

        if (!window.confirm('Are you sure you want to delete this notification? This will remove all email settings for this customer.')) {
            return
        }

        setDeleting(true)
        try {
            await CustomerService.deleteNotification({ GroupId: groupId })
            successToast('Notification deleted successfully')
            setEmailList([])
            setAutoEmail(false)
            setGroupId(null)
            onClose(true)
        } catch (error) {
            console.error('Error deleting notification:', error)
            errorToast('Failed to delete notification')
        } finally {
            setDeleting(false)
        }
    }

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            handleAddEmail()
        }
    }

    const handleSubmit = async () => {
        setSaving(true)
        try {
            const otherEmailsString = emailList.join('; ')
            const payload = {
                CustomerId: customer.unique_id,
                OtherEmails: otherEmailsString,
                AutoEmail: autoEmail,
                CompanyId: companyId,
                SystemUserId: systemUserId,
            }

            await CustomerService.saveNotification(payload)
            successToast('Notification settings saved successfully')
            onClose(true)
        } catch (error) {
            console.error('Error saving notification:', error)
            errorToast('Failed to save notification settings')
        } finally {
            setSaving(false)
        }
    }

    const handleClose = () => {
        setEmailInput('')
        onClose(false)
    }

    return (
        <Modal
            open={open}
            onClose={handleClose}
            size="small"
            className="model"
        >
            <Modal.Header>
                <FlexRowBetween>
                    <TitleText>Set Auto Email</TitleText>
                    <X
                        className="pointer"
                        size={32}
                        onClick={handleClose}
                    />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content>
                {loading ? (
                    <LoaderBar content="Loading notification settings..." />
                ) : (
                    <>
                        <Form>
                            <Form.Field>
                                <label>Auto Email</label>
                                <Checkbox
                                    checked={autoEmail}
                                    onChange={(e, { checked }) => setAutoEmail(checked)}
                                    label="Enable auto email notifications"
                                />
                            </Form.Field>
                            <Form.Field>
                                <label>Email Addresses</label>
                                <Input
                                    placeholder="Enter email address"
                                    value={emailInput}
                                    onChange={(e) => setEmailInput(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    action={{
                                        content: 'Add',
                                        onClick: handleAddEmail,
                                    }}
                                />
                            </Form.Field>
                        </Form>
                        {emailList.length > 0 && (
                            <EmailListContainer>
                                {emailList.map((email, index) => (
                                    <EmailItem key={index}>
                                        <Checkbox checked readOnly />
                                        <EmailText>{email}</EmailText>
                                        <DeleteIcon
                                            size={18}
                                            onClick={() => handleDeleteEmail(email)}
                                        />
                                    </EmailItem>
                                ))}
                            </EmailListContainer>
                        )}
                    </>
                )}
            </Modal.Content>
            <Modal.Actions>
                {groupId && (
                    <AppButton
                        type="button"
                        onClick={handleDeleteNotification}
                        loading={deleting}
                        disabled={saving || loading || deleting}
                        style={{ backgroundColor: '#dc3545', color: 'white', marginRight: 'auto' }}
                    >
                        Delete Notification
                    </AppButton>
                )}
                <AppButton type="button" onClick={handleClose} disabled={saving || deleting}>
                    Cancel
                </AppButton>
                <AppPrimaryButton
                    type="button"
                    onClick={handleSubmit}
                    loading={saving}
                    disabled={saving || loading || deleting}
                >
                    Save
                </AppPrimaryButton>
            </Modal.Actions>
        </Modal>
    )
}
